/**
 * Huddles: the call state machine, the cached active huddles (one per conversation), the participant grid, speaking
 * detection, device selection, and the per-user media preferences (noise suppression, echo cancellation, auto gain,
 * background blur/image, mirror, join defaults). Pure and translated; the media itself is the platform CallEngine.
 */
import type { CreateMeetingRequest, Huddle, HuddleCaption, HuddleDrawOp, HuddleParticipant, HuddleRecap, HuddleTheme, HuddleTracks, MeetingAccess, MeetingChatMessage, Member, PersistentEvent } from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";
import { memberAvatar } from "./profile.ts";

// ---------- Preferences ----------

/** off: raw mic; standard: the platform's built-in suppression; strong: a neural filter (RNNoise / Voice Isolation). */
export type NoiseSuppression = "off" | "standard" | "strong";
export type BlurStrength = "light" | "strong";
/** `image`: a bundled background (`BACKGROUND_IMAGES`) or "custom" (the person's own picture, kept on the device). */
export type BackgroundEffect = { kind: "none" } | { kind: "blur"; strength: BlurStrength } | { kind: "image"; imageId: string };
export type DeviceKind = "audioinput" | "videoinput" | "audiooutput";

export type HuddlePrefs = {
  noiseSuppression: NoiseSuppression;
  echoCancellation: boolean;
  autoGainControl: boolean;
  background: BackgroundEffect;
  /** Show my own video mirrored (others always see it unmirrored). */
  mirrorVideo: boolean;
  joinWithMicOff: boolean;
  joinWithCameraOff: boolean;
  /** Preferred device ids; a missing device falls back to the system default (see resolveDevice). */
  audioinput?: string;
  videoinput?: string;
  audiooutput?: string;
};

export const DEFAULT_PREFS: HuddlePrefs = {
  noiseSuppression: "standard",
  echoCancellation: true,
  autoGainControl: true,
  background: { kind: "none" },
  mirrorVideo: true,
  joinWithMicOff: false,
  joinWithCameraOff: true,
};

/** Bundled background pictures (assets named `huddle-bg-<id>` on every platform). */
export const BACKGROUND_IMAGES = ["dusk", "paper", "studio"];
export const CUSTOM_BACKGROUND = "custom";

/** Device-local storage key: preferences are per signed-in person. */
export function prefsKey(userId: string): string {
  return `bundle.huddle.prefs.${userId}`;
}

function isBackground(v: unknown): v is BackgroundEffect {
  if (typeof v !== "object" || v === null) return false;
  const o = v as Record<string, unknown>;
  if (o.kind === "none") return true;
  if (o.kind === "blur") return o.strength === "light" || o.strength === "strong";
  if (o.kind === "image") return typeof o.imageId === "string" && (BACKGROUND_IMAGES.includes(o.imageId) || o.imageId === CUSTOM_BACKGROUND);
  return false;
}

/** Stored JSON → preferences; anything missing or invalid takes its default. */
export function parsePrefs(raw: string | undefined): HuddlePrefs {
  let o: Record<string, unknown> = {};
  if (raw !== undefined) {
    try {
      const v: unknown = JSON.parse(raw);
      if (typeof v === "object" && v !== null && !Array.isArray(v)) o = v as Record<string, unknown>;
    } catch {
      o = {};
    }
  }
  const bool = (k: string, d: boolean): boolean => (typeof o[k] === "boolean" ? (o[k] as boolean) : d);
  const ns = o.noiseSuppression;
  const prefs: HuddlePrefs = {
    noiseSuppression: ns === "off" || ns === "standard" || ns === "strong" ? ns : DEFAULT_PREFS.noiseSuppression,
    echoCancellation: bool("echoCancellation", DEFAULT_PREFS.echoCancellation),
    autoGainControl: bool("autoGainControl", DEFAULT_PREFS.autoGainControl),
    background: isBackground(o.background) ? o.background : DEFAULT_PREFS.background,
    mirrorVideo: bool("mirrorVideo", DEFAULT_PREFS.mirrorVideo),
    joinWithMicOff: bool("joinWithMicOff", DEFAULT_PREFS.joinWithMicOff),
    joinWithCameraOff: bool("joinWithCameraOff", DEFAULT_PREFS.joinWithCameraOff),
  };
  for (const k of ["audioinput", "videoinput", "audiooutput"] as const) {
    const id = o[k];
    if (typeof id === "string" && id !== "") prefs[k] = id;
  }
  return prefs;
}

/** Prefers `id` for a device kind (undefined = follow the system default). */
export function withDevice(prefs: HuddlePrefs, kind: DeviceKind, id: string | undefined): HuddlePrefs {
  const next: HuddlePrefs = { ...prefs };
  if (id === undefined || id === "") delete next[kind];
  else next[kind] = id;
  return next;
}

// ---------- Processing derived from preferences ----------

/**
 * Mic capture settings. "strong" turns the built-in suppressor off and runs the neural filter instead (stacking both
 * makes speech sound underwater).
 */
export type AudioProcessing = { echoCancellation: boolean; autoGainControl: boolean; noiseSuppression: boolean; noiseFilter: "none" | "rnnoise" };

export function audioProcessing(p: HuddlePrefs): AudioProcessing {
  return {
    echoCancellation: p.echoCancellation,
    autoGainControl: p.autoGainControl,
    noiseSuppression: p.noiseSuppression === "standard",
    noiseFilter: p.noiseSuppression === "strong" ? "rnnoise" : "none",
  };
}

export type VideoProcessing = { mode: "none" } | { mode: "blur"; radius: number } | { mode: "image"; imageId: string };

/** Blur radius in pixels at 640×360 (light keeps the room recognisable, strong hides it). */
export const BLUR_RADIUS: Record<BlurStrength, number> = { light: 6, strong: 16 };

export function videoProcessing(bg: BackgroundEffect): VideoProcessing {
  if (bg.kind === "blur") return { mode: "blur", radius: BLUR_RADIUS[bg.strength] };
  if (bg.kind === "image") return { mode: "image", imageId: bg.imageId };
  return { mode: "none" };
}

export function sameBackground(a: BackgroundEffect, b: BackgroundEffect): boolean {
  if (a.kind === "blur" && b.kind === "blur") return a.strength === b.strength;
  if (a.kind === "image" && b.kind === "image") return a.imageId === b.imageId;
  return a.kind === b.kind;
}

export type BackgroundOption = { id: string; effect: BackgroundEffect; label: MessageDescriptor; selected: boolean };

/** The background picker: none, two blurs, the bundled pictures, then my own picture when I have one. */
export function backgroundOptions(selected: BackgroundEffect, hasCustom: boolean): BackgroundOption[] {
  const effects: { id: string; effect: BackgroundEffect; label: MessageDescriptor }[] = [
    { id: "none", effect: { kind: "none" }, label: msg("huddle.background.none") },
    { id: "blur-light", effect: { kind: "blur", strength: "light" }, label: msg("huddle.background.blurLight") },
    { id: "blur-strong", effect: { kind: "blur", strength: "strong" }, label: msg("huddle.background.blurStrong") },
  ];
  for (const id of BACKGROUND_IMAGES) {
    effects.push({ id: `image-${id}`, effect: { kind: "image", imageId: id }, label: msg(`huddle.background.image.${id}` as "huddle.background.image.dusk") });
  }
  if (hasCustom) effects.push({ id: "image-custom", effect: { kind: "image", imageId: CUSTOM_BACKGROUND }, label: msg("huddle.background.custom") });
  return effects.map((e) => ({ ...e, selected: sameBackground(e.effect, selected) }));
}

// ---------- Devices ----------

export type CallDevice = { id: string; kind: DeviceKind; label: string };
/** A picker row: the device's own name, or `fallback` ("Microphone 2") when the platform hides it. */
export type DeviceOption = { id: string; label?: string; fallback: MessageDescriptor; selected: boolean };

/** The device in use: the preferred one when plugged in, else the system default, else the first; undefined = none. */
export function resolveDevice(devices: CallDevice[], kind: DeviceKind, preferred: string | undefined): string | undefined {
  const ofKind = devices.filter((d) => d.kind === kind);
  if (preferred !== undefined && ofKind.some((d) => d.id === preferred)) return preferred;
  if (ofKind.some((d) => d.id === "default")) return "default";
  return ofKind[0]?.id;
}

/** Picker rows for one kind, in platform order (unnamed devices, e.g. before permission is granted, get a fallback). */
export function deviceOptions(devices: CallDevice[], kind: DeviceKind, selectedId: string | undefined): DeviceOption[] {
  const key = kind === "audioinput" ? "huddle.device.microphone" : kind === "videoinput" ? "huddle.device.camera" : "huddle.device.speaker";
  return devices
    .filter((d) => d.kind === kind)
    .map((d, i) => {
      const o: DeviceOption = { id: d.id, fallback: msg(key, { n: String(i + 1) }), selected: d.id === selectedId };
      if (d.label !== "") o.label = d.label;
      return o;
    });
}

// ---------- Cached active huddles (WorkspaceCache.huddles, keyed by conversation) ----------

/** Keeps the newer snapshot: a different huddle replaces the old one; the same huddle only moves forward. */
export function upsertHuddle(huddles: Record<string, Huddle>, h: Huddle): Record<string, Huddle> {
  const cur = huddles[h.channelId];
  if (cur !== undefined && cur.id === h.id && cur.version >= h.version) return huddles;
  return { ...huddles, [h.channelId]: h };
}

export function removeHuddle(huddles: Record<string, Huddle>, channelId: string, huddleId: string): Record<string, Huddle> {
  const cur = huddles[channelId];
  if (cur === undefined || cur.id !== huddleId) return huddles;
  const next = { ...huddles };
  delete next[channelId];
  return next;
}

export function huddlesFromList(list: Huddle[] | undefined): Record<string, Huddle> {
  let out: Record<string, Huddle> = {};
  for (const h of list ?? []) out = upsertHuddle(out, h);
  return out;
}

/** huddle.* events → the cache (other events leave it unchanged). */
export function applyHuddleEvent(huddles: Record<string, Huddle>, ev: PersistentEvent): Record<string, Huddle> {
  switch (ev.type) {
    case "huddle.started":
    case "huddle.updated":
      return upsertHuddle(huddles, ev.huddle);
    case "huddle.ended":
      return removeHuddle(huddles, ev.channelId, ev.huddleId);
    default:
      return huddles;
  }
}

export function participant(h: Huddle | undefined, userId: string): HuddleParticipant | undefined {
  return h?.participants.find((p) => p.userId === userId);
}

/** "Ada, Bo and 2 others" style summary for the channel header chip and the huddle pill. */
export function huddleSummary(h: Huddle, members: Record<string, Member>, meId: string): MessageDescriptor {
  const names = h.participants.map((p) => (p.userId === meId ? undefined : members[p.userId]?.displayName)).filter((n): n is string => n !== undefined);
  const inIt = h.participants.some((p) => p.userId === meId);
  if (names.length === 0) return inIt ? msg("huddle.summary.justYou") : msg("huddle.summary.count", { count: h.participants.length });
  if (names.length === 1) return msg("huddle.summary.one", { name: names[0]! });
  if (names.length === 2) return msg("huddle.summary.two", { a: names[0]!, b: names[1]! });
  return msg("huddle.summary.many", { a: names[0]!, count: String(names.length - 1) });
}

// ---------- Call state machine ----------

export type CallStatus = "idle" | "joining" | "connecting" | "connected" | "reconnecting" | "leaving";
export type CallError = "forbidden" | "notFound" | "network" | "ended" | "micDenied" | "cameraDenied" | "screenDenied" | "media";

export type CallMachine = { status: CallStatus; channelId?: string; huddleId?: string; error?: CallError };

/** `media`: the engine's connection; "unavailable" = this deployment carries no media (state-only huddle). */
export type CallEvent =
  | { type: "join"; channelId: string }
  | { type: "joined"; huddleId: string }
  | { type: "media"; connection: "connecting" | "connected" | "reconnecting" | "failed" | "unavailable" }
  | { type: "leave" }
  | { type: "left" }
  | { type: "ended" }
  | { type: "failed"; error: CallError };

export const IDLE_CALL: CallMachine = { status: "idle" };

/** Transitions; events that don't apply in the current state are ignored (returns the same state). */
export function callTransition(s: CallMachine, e: CallEvent): CallMachine {
  switch (e.type) {
    case "join":
      if (s.status !== "idle") return s;
      return { status: "joining", channelId: e.channelId };
    case "joined":
      if (s.status !== "joining" || s.channelId === undefined) return s;
      return { status: "connecting", channelId: s.channelId, huddleId: e.huddleId };
    case "media": {
      if (s.channelId === undefined || s.huddleId === undefined) return s;
      if (s.status !== "connecting" && s.status !== "connected" && s.status !== "reconnecting") return s;
      const base = { channelId: s.channelId, huddleId: s.huddleId };
      if (e.connection === "connected" || e.connection === "unavailable") return { status: "connected", ...base };
      if (e.connection === "failed") return { status: "idle", error: "media" };
      if (e.connection === "reconnecting" || (e.connection === "connecting" && s.status === "connected")) return { status: "reconnecting", ...base };
      return { status: s.status === "reconnecting" ? "reconnecting" : "connecting", ...base };
    }
    case "leave":
      if (s.status === "idle" || s.status === "leaving") return s;
      return s.huddleId === undefined ? IDLE_CALL : { status: "leaving", channelId: s.channelId!, huddleId: s.huddleId };
    case "left":
      return s.status === "leaving" ? IDLE_CALL : s;
    case "ended":
      if (s.status === "idle") return s;
      return { status: "idle", error: "ended" };
    case "failed":
      return { status: "idle", error: e.error };
  }
}

/** In a huddle (or getting there): the bar, pill and call pane are shown. */
export function inCall(s: CallMachine): boolean {
  return s.status !== "idle";
}

/** `app`: the native apps, whose permission lives in the system Settings (the web says "browser or system settings"). */
export function callErrorMessage(e: CallError, app = false): MessageDescriptor {
  if (app && (e === "micDenied" || e === "cameraDenied")) return msg(`huddle.error.${e}App`);
  return msg(`huddle.error.${e}` as "huddle.error.media");
}

// ---------- Participants, grid, speaking ----------

/** A member's profile photo (their chosen avatar, else `avatarUrl` from older servers); none = initials. */
function photoOf(m: Member | undefined): string | undefined {
  const a = m === undefined ? undefined : memberAvatar(m);
  return a?.kind === "photo" ? a.url : undefined;
}

export type CallTile = {
  userId: string;
  name: string;
  avatarUrl?: string;
  isMe: boolean;
  isAgent: boolean;
  /** A guest from a meeting link (External badge; no profile). */
  external: boolean;
  muted: boolean;
  video: boolean;
  screen: boolean;
  handRaised: boolean;
  speaking: boolean;
  /** An emoji reaction floating over the tile right now (trackReactions). */
  reaction?: string;
};

/**
 * Tiles in grid order: people sharing their screen first, then raised hands (in raise order), then everyone in join
 * order. My own state comes from `me` (applied optimistically before the server confirms).
 */
export function callTiles(
  h: Huddle,
  members: Record<string, Member>,
  meId: string,
  speaking: string[],
  me: { muted: boolean; video: boolean; screen: boolean; hand: boolean } | undefined,
  reactions: Record<string, string> = {},
): CallTile[] {
  const tiles = h.participants.map((p, i) => {
    const m = members[p.userId];
    const mine = p.userId === meId && me !== undefined;
    const t: CallTile = {
      userId: p.userId,
      name: p.kind === "guest" ? (p.name ?? "") : (m?.displayName ?? p.name ?? ""),
      isMe: p.userId === meId,
      isAgent: m?.kind === "agent",
      external: p.kind === "guest",
      muted: mine ? me.muted : p.muted,
      video: mine ? me.video : p.video,
      screen: mine ? me.screen : p.screen,
      handRaised: mine ? me.hand : p.handRaisedAt !== undefined,
      speaking: speaking.includes(p.userId) && !(mine ? me.muted : p.muted),
    };
    const photo = photoOf(m);
    if (photo !== undefined) t.avatarUrl = photo;
    const r = reactions[p.userId];
    if (r !== undefined) t.reaction = r;
    return { t, i, hand: p.handRaisedAt ?? Number.MAX_SAFE_INTEGER };
  });
  const rank = (x: { t: CallTile }) => (x.t.screen ? 0 : x.t.handRaised ? 1 : 2);
  return tiles
    .toSorted((a, b) => rank(a) - rank(b) || (rank(a) === 1 ? a.hand - b.hand : 0) || a.i - b.i)
    .map((x) => x.t);
}

/** The screen being shown big: the first person sharing (undefined = plain grid). */
export function stageUserId(tiles: CallTile[]): string | undefined {
  return tiles.find((t) => t.screen)?.userId;
}

/** Grid columns for n tiles (1, 2, 2, 2, 3 … 3, then 4). */
export function gridColumns(n: number): number {
  if (n <= 1) return 1;
  if (n <= 4) return 2;
  if (n <= 9) return 3;
  return 4;
}

/** Audio level (0–1, RMS) at or above which someone counts as speaking; they stay "speaking" for SPEAKING_HOLD_MS. */
export const SPEAKING_LEVEL = 0.04;
export const SPEAKING_HOLD_MS = 600;

/** Records when each source was last loud. Keys are user ids. */
export function updateSpeaking(lastLoud: Record<string, number>, levels: Record<string, number>, now: number): Record<string, number> {
  const next: Record<string, number> = {};
  for (const [id, at] of Object.entries(lastLoud)) if (now - at < SPEAKING_HOLD_MS) next[id] = at;
  for (const [id, level] of Object.entries(levels)) if (level >= SPEAKING_LEVEL) next[id] = now;
  return next;
}

/** Who is speaking now, sorted for stable comparison. */
export function speakingIds(lastLoud: Record<string, number>, now: number): string[] {
  return Object.entries(lastLoud)
    .filter(([, at]) => now - at < SPEAKING_HOLD_MS)
    .map(([id]) => id)
    .toSorted();
}

/** Lit bars of an input meter: a level in dB from -60 (silent) to 0 (full scale) spread over `bars`. */
export function levelBars(level: number, bars: number): number {
  if (!(level > 0)) return 0;
  const db = 20 * Math.log10(Math.min(level, 1));
  const lit = Math.round(((db + 60) / 60) * bars);
  return Math.max(0, Math.min(bars, lit));
}

/** Track refs that differ (the client announces its published tracks when they change). */
export function sameTracks(a: HuddleTracks, b: HuddleTracks): boolean {
  for (const k of ["mic", "camera", "screen", "screenAudio"] as const) {
    const x = a[k];
    const y = b[k];
    if ((x === undefined) !== (y === undefined)) return false;
    if (x !== undefined && y !== undefined && (x.sessionId !== y.sessionId || x.trackName !== y.trackName)) return false;
  }
  return true;
}

/** Heartbeat interval (the server drops participants after 45 s without one). */
export const HEARTBEAT_MS = 15_000;

// ---------- External huddles (meeting links) ----------

/** May `meId` moderate `target` (mute / remove)? Members act on guests; the host on anyone. Never on themselves. */
export function canModerate(h: Huddle, meId: string, targetId: string): boolean {
  if (targetId === meId) return false;
  const me = participant(h, meId);
  const target = participant(h, targetId);
  if (me === undefined || target === undefined || me.kind === "guest") return false;
  return target.kind === "guest" || isHost(h, meId);
}

/** The meeting's host, or for a plain huddle whoever started it. */
export function isHost(h: Huddle, meId: string): boolean {
  return (h.meeting?.hostId ?? h.startedBy) === meId;
}

/** "Ana wants to join" / "Ana and 2 others want to join" for the lobby banner (undefined = nobody waiting). */
export function lobbyLabel(h: Huddle | undefined): MessageDescriptor | undefined {
  const lobby = h?.lobby ?? [];
  if (lobby.length === 0) return undefined;
  if (lobby.length === 1) return msg("huddle.lobby.one", { name: lobby[0]!.name });
  return msg("huddle.lobby.many", { name: lobby[0]!.name, count: String(lobby.length - 1) });
}

/** Appends new chat messages (by seq, no duplicates) and keeps the newest `max`. */
export function mergeChat(existing: MeetingChatMessage[], incoming: MeetingChatMessage[], max: number): MeetingChatMessage[] {
  if (incoming.length === 0) return existing;
  const seen = new Set(existing.map((m) => m.seq));
  const merged = [...existing, ...incoming.filter((m) => !seen.has(m.seq))].toSorted((a, b) => a.seq - b.seq);
  return merged.length > max ? merged.slice(merged.length - max) : merged;
}

/** Cursor for the next chat poll. */
export function chatCursor(chat: MeetingChatMessage[]): number {
  return chat.length === 0 ? 0 : chat[chat.length - 1]!.seq;
}

/** A trimmed guest name, or undefined when it isn't usable (1–80 characters). */
export function guestName(raw: string): string | undefined {
  const name = raw.trim().replace(/\s+/g, " ");
  return name.length >= 1 && name.length <= 80 ? name : undefined;
}

/** The link-page call phases: before joining (preview), knocking, waiting in the lobby, in the call, or out. */
export type MeetingPhase = "loading" | "unavailable" | "preview" | "joining" | "waiting" | "admitted" | "denied" | "removed" | "ended" | "left";

/** The "New external huddle" form. */
export type NewMeetingForm = {
  title: string;
  /** The conversation the huddle runs in (members see it there); absent for a standalone meeting. */
  channelId?: string;
  when: "now" | "later";
  /** "HH:MM" (24 h, local time) when scheduling for later. */
  time: string;
  /** Guests join without waiting in the lobby. */
  open: boolean;
};

/** "14:30" → the next such moment in local time (today, or tomorrow once it has passed); undefined when malformed. */
export function nextTimeAt(time: string, now: number): number | undefined {
  const m = /^\s*(\d{1,2})[:h.](\d{2})\s*$/.exec(time);
  if (m === null) return undefined;
  const hours = Number(m[1]);
  const minutes = Number(m[2]);
  if (hours > 23 || minutes > 59) return undefined;
  const at = new Date(now);
  at.setHours(hours, minutes, 0, 0);
  if (at.getTime() <= now) at.setDate(at.getDate() + 1);
  return at.getTime();
}

/** The create request for a filled-in form, or undefined while it isn't valid (no title, bad time). */
export function newMeetingRequest(form: NewMeetingForm, now: number): CreateMeetingRequest | undefined {
  const title = form.title.trim();
  if (title === "" || title.length > 120) return undefined;
  const req: CreateMeetingRequest = { title, policy: form.open ? "open" : "ask" };
  if (form.channelId !== undefined) req.channelId = form.channelId;
  if (form.when === "later") {
    const at = nextTimeAt(form.time, now);
    if (at === undefined) return undefined;
    req.scheduledAt = at;
  }
  return req;
}

/** The meeting code in a link's path ("/m/abc-defg-hij"), or undefined for any other path. */
export function meetingCodeFromPath(path: string): string | undefined {
  const m = /^\/m\/([a-z]{3}-[a-z]{4}-[a-z]{3})\/?$/.exec(path.split("?")[0]!.split("#")[0]!);
  return m?.[1];
}

/** Server access → phase (keeps "left" once I've left). */
export function meetingPhase(current: MeetingPhase, access: MeetingAccess): MeetingPhase {
  if (current === "left") return current;
  return access;
}

// ---------- Ringing people in (Slack "Invite people" / an incoming huddle) ----------

/** How long an incoming ring stays up when nobody answers (Slack rings about this long). */
export const RING_TIMEOUT_MS = 30_000;
/** At most this many people per ring (contracts RingHuddleRequest). */
export const RING_MAX = 20;

/** Someone in the conversation I can ring into my huddle. */
export type RingCandidate = { userId: string; name: string; avatarUrl?: string };

/**
 * "Invite people" in a huddle: the conversation's members I can ring — active people (no agents), not me, not already
 * in the call — sorted by name; `query` matches the display or full name.
 */
export function ringCandidates(members: Record<string, Member>, memberIds: string[], huddle: Huddle | undefined, meId: string, query: string): RingCandidate[] {
  const inCall = new Set((huddle?.participants ?? []).map((p) => p.userId));
  const q = query.trim().toLocaleLowerCase();
  const out: RingCandidate[] = [];
  for (const id of new Set(memberIds)) {
    const m = members[id];
    if (m === undefined || id === meId || inCall.has(id) || m.kind !== "human" || m.deactivated) continue;
    if (q !== "" && !m.displayName.toLocaleLowerCase().includes(q) && !m.fullName.toLocaleLowerCase().includes(q)) continue;
    const c: RingCandidate = { userId: id, name: m.displayName };
    const photo = photoOf(m);
    if (photo !== undefined) c.avatarUrl = photo;
    out.push(c);
  }
  return out.sort((a, b) => (a.name.toLocaleLowerCase() < b.name.toLocaleLowerCase() ? -1 : a.name.toLocaleLowerCase() > b.name.toLocaleLowerCase() ? 1 : a.userId < b.userId ? -1 : 1));
}

/** People picked in "Invite people", toggled (at most RING_MAX). */
export function toggleRingPick(picked: string[], userId: string): string[] {
  if (picked.includes(userId)) return picked.filter((u) => u !== userId);
  return picked.length >= RING_MAX ? picked : [...picked, userId];
}

/** Someone is ringing me into a huddle (accept = join it, decline = dismiss). */
export type IncomingRing = { channelId: string; huddleId: string; callerId: string; title: string; body: string; url: string; at: number };

/**
 * A realtime `notification` about a huddle (someone started one in my DM, or rang me) → the incoming ring to show, or
 * undefined: not about a huddle, my own, or I'm already in that conversation's call.
 */
export function incomingRing(event: { about?: string; userId: string; channelId: string; messageId: string; title: string; body: string; url: string; ts: number }, meId: string, inCallChannelId: string | undefined): IncomingRing | undefined {
  if (event.about !== "huddle" || event.userId === meId || event.channelId === inCallChannelId) return undefined;
  return { channelId: event.channelId, huddleId: event.messageId, callerId: event.userId, title: event.title, body: event.body, url: event.url, at: event.ts };
}

/**
 * The ring is over: unanswered for RING_TIMEOUT_MS (from `shownAt`, when it reached me), the huddle ended — its
 * snapshot was seen (`seen`) and is gone or replaced (the snapshot may arrive after the ring) — or I answered it on
 * another device (`meId` is in it; Google Meet stops ringing everywhere once one device picks up).
 */
export function ringExpired(ring: IncomingRing, shownAt: number, now: number, huddleNow: Huddle | undefined, seen: boolean, meId: string): boolean {
  if (now - shownAt >= RING_TIMEOUT_MS) return true;
  if (huddleNow !== undefined && huddleNow.id === ring.huddleId && huddleNow.participants.some((p) => p.userId === meId)) return true;
  return seen && (huddleNow === undefined || huddleNow.id !== ring.huddleId);
}

/**
 * "Ana declined": a realtime `notification` saying someone turned down my ring (contracts `declinedBy`; the event's
 * `userId` is my own id) → who and which huddle, or undefined for any other notification.
 */
export function ringDeclined(event: { about?: string; userId: string; channelId: string; messageId: string; declinedBy?: string; ts: number }, meId: string): { userId: string; channelId: string; huddleId: string; at: number } | undefined {
  if (event.about !== "huddle" || event.declinedBy === undefined || event.userId !== meId || event.declinedBy === meId) return undefined;
  return { userId: event.declinedBy, channelId: event.channelId, huddleId: event.messageId, at: event.ts };
}

/** How long "Ana declined" stays up in the call. */
export const DECLINED_NOTICE_MS = 5000;

// ---------- Missed huddles (Slack's "Missed huddle" line with Call back) ----------

/** A huddle I was rung into and never joined: who to call back. */
export type MissedHuddle = { callerId: string };

/**
 * The huddle message → "Missed huddle" for me, or undefined: still going, I was in it, or nobody rang me. Durable
 * (the message's `missed`, written when the huddle ends), so it also shows when the app was closed during the ring.
 * Huddle messages from before `missed` existed: a 1:1 DM huddle I wasn't in counts as missed, calling back its author
 * (`oneToOne`: the conversation is a 1:1 DM; `authorId`: the huddle message's author, who started it).
 */
export function missedHuddle(recap: HuddleRecap, meId: string, legacy?: { oneToOne: boolean; authorId: string }): MissedHuddle | undefined {
  if (recap.endedAt === undefined || recap.participantIds.includes(meId)) return undefined;
  if (recap.missed !== undefined) {
    const mine = recap.missed.find((r) => r.userId === meId);
    return mine === undefined || mine.callerId === meId ? undefined : { callerId: mine.callerId };
  }
  if (legacy === undefined || !legacy.oneToOne || legacy.authorId === meId) return undefined;
  return { callerId: legacy.authorId };
}

// ---------- Reactions in the call (Slack huddle reactions) ----------

/** The quick-reaction bar in the call (Slack's huddle reactions). */
export const HUDDLE_REACTIONS = ["👍", "👏", "😂", "🎉", "❤️", "👋", "🙌", "😮"] as const;
/** How long a reaction floats over the tile. */
export const HUDDLE_REACTION_MS = 4000;

/** A participant's latest reaction and when this client first saw it (`shownAt`). */
export type ShownReaction = { emoji: string; at: number; shownAt: number };

/**
 * Every participant's latest reaction with when this client first saw it. A reaction floats over the tile for
 * HUDDLE_REACTION_MS from then (server `at` times aren't compared with the local clock): a new snapshot with the same
 * `at` keeps `shownAt`, a new reaction (another `at`) starts again. Reactions already there when I join (`initial`)
 * count as over.
 */
export function trackReactions(prev: Record<string, ShownReaction>, h: Huddle | undefined, now: number, initial = false): Record<string, ShownReaction> {
  const out: Record<string, ShownReaction> = {};
  for (const p of h?.participants ?? []) {
    const r = p.reaction;
    if (r === undefined) continue;
    const before = prev[p.userId];
    out[p.userId] = before !== undefined && before.at === r.at ? before : { emoji: r.emoji, at: r.at, shownAt: initial ? now - HUDDLE_REACTION_MS : now };
  }
  return out;
}

/** The reactions floating now, by participant. */
export function activeReactions(seen: Record<string, ShownReaction>, now: number): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [userId, r] of Object.entries(seen)) if (now - r.shownAt < HUDDLE_REACTION_MS && now >= r.shownAt) out[userId] = r.emoji;
  return out;
}

/** When the next floating reaction goes away (undefined = none floating). */
export function nextReactionExpiry(seen: Record<string, ShownReaction>, now: number): number | undefined {
  let next: number | undefined;
  for (const r of Object.values(seen)) {
    const end = r.shownAt + HUDDLE_REACTION_MS;
    if (end > now && (next === undefined || end < next)) next = end;
  }
  return next;
}

// ---------- Effects: emoji bursts and themes (Slack huddle effects) ----------

/** The emoji effects picker: each bursts across the whole call for everyone. */
export const HUDDLE_EFFECTS = ["🎉", "❤️", "🔥", "👏", "😂", "🚀", "⭐", "🎈"] as const;
/** How long an effect's burst lasts. */
export const HUDDLE_EFFECT_MS = 3000;
/** Emoji in one burst (reduced motion shows one, still, instead). */
export const HUDDLE_EFFECT_PARTICLES = 16;

/** A participant's latest effect, when this client first saw it, and the seed of its burst (server `at`). */
export type ShownEffect = { emoji: string; at: number; shownAt: number; seed: number };

/**
 * One emoji of a burst, every value a fraction 0…1 for the renderer to scale: `x` where it starts along the bottom,
 * `delay` (of the burst's first 40 %), `drift` sideways (0.5 = straight up), `size`, `spin` (0.5 = none), `speed`.
 */
export type EffectParticle = { x: number; delay: number; drift: number; size: number; spin: number; speed: number };

/** An effect bursting now. `key` stays the same for the whole burst (renderers animate it once per key). */
export type CallEffect = { key: string; userId: string; emoji: string; shownAt: number; particles: EffectParticle[] };

/**
 * The layout of a burst, from its seed: a 32-bit LCG (Numerical Recipes constants) so every platform computes the same
 * numbers; values are rounded to 3 decimals.
 */
export function effectBurst(seed: number, count = HUDDLE_EFFECT_PARTICLES): EffectParticle[] {
  let a = seed >>> 0;
  const next = (): number => {
    a = (Math.imul(a, 1664525) + 1013904223) >>> 0;
    return Math.round((a / 4294967296) * 1000) / 1000;
  };
  const out: EffectParticle[] = [];
  for (let i = 0; i < count; i++) out.push({ x: next(), delay: next(), drift: next(), size: next(), spin: next(), speed: next() });
  return out;
}

/**
 * Every participant's latest effect with when this client first saw it (same rules as trackReactions: a new `at`
 * starts a burst, the same one keeps its start, effects already there when I join count as over).
 */
export function trackEffects(prev: Record<string, ShownEffect>, h: Huddle | undefined, now: number, initial = false): Record<string, ShownEffect> {
  const out: Record<string, ShownEffect> = {};
  for (const p of h?.participants ?? []) {
    const e = p.effect;
    if (e === undefined) continue;
    const before = prev[p.userId];
    out[p.userId] = before !== undefined && before.at === e.at ? before : { emoji: e.emoji, at: e.at, shownAt: initial ? now - HUDDLE_EFFECT_MS : now, seed: e.at };
  }
  return out;
}

/** The bursts playing now, oldest first. */
export function activeEffects(seen: Record<string, ShownEffect>, now: number): CallEffect[] {
  return Object.entries(seen)
    .filter(([, e]) => now - e.shownAt < HUDDLE_EFFECT_MS && now >= e.shownAt)
    .toSorted(([ua, a], [ub, b]) => a.shownAt - b.shownAt || (ua < ub ? -1 : ua > ub ? 1 : 0))
    .map(([userId, e]) => ({ key: `${userId}:${e.seed}`, userId, emoji: e.emoji, shownAt: e.shownAt, particles: effectBurst(e.seed) }));
}

/** When the next burst is over (undefined = none playing). */
export function nextEffectExpiry(seen: Record<string, ShownEffect>, now: number): number | undefined {
  let next: number | undefined;
  for (const e of Object.values(seen)) {
    const end = e.shownAt + HUDDLE_EFFECT_MS;
    if (end > now && (next === undefined || end < next)) next = end;
  }
  return next;
}

/** A call theme: its id, name, and backdrop gradient (top to bottom; dark enough for white text and tiles). */
export type ThemeSpec = { id: Exclude<HuddleTheme, "none">; label: MessageDescriptor; colors: [string, string, string] };

/** The themes, in picker order. */
export const HUDDLE_THEMES: ThemeSpec[] = [
  { id: "sunrise", label: msg("huddle.theme.sunrise"), colors: ["#2E1A47", "#A23B5C", "#F28C5B"] },
  { id: "ocean", label: msg("huddle.theme.ocean"), colors: ["#0B1D3A", "#0E4D74", "#1A8FA6"] },
  { id: "forest", label: msg("huddle.theme.forest"), colors: ["#0F2418", "#1F4D2E", "#4C7A3A"] },
  { id: "galaxy", label: msg("huddle.theme.galaxy"), colors: ["#0B0B1E", "#2A1659", "#6B2F8F"] },
  { id: "party", label: msg("huddle.theme.party"), colors: ["#3B0A45", "#C0267A", "#F5A623"] },
];

/** The theme to draw (undefined = the plain call). */
export function huddleTheme(id: HuddleTheme | undefined): ThemeSpec | undefined {
  return HUDDLE_THEMES.find((t) => t.id === id);
}

export type ThemeOption = { id: HuddleTheme; label: MessageDescriptor; colors?: [string, string, string]; selected: boolean };

/** The theme picker: None, then every theme; the call's current one is selected. */
export function themeOptions(current: HuddleTheme | undefined): ThemeOption[] {
  const cur = huddleTheme(current)?.id ?? "none";
  return [
    { id: "none", label: msg("huddle.theme.none"), selected: cur === "none" },
    ...HUDDLE_THEMES.map((t): ThemeOption => ({ id: t.id, label: t.label, colors: t.colors, selected: cur === t.id })),
  ];
}

// ---------- Live captions (Slack huddle captions) ----------

/**
 * The call is transcribed: the workspace transcribes its huddles, or anyone has captions on. Then every client
 * transcribes its own microphone while unmuted (never a muted mic).
 */
export function transcribing(h: Huddle | undefined): boolean {
  if (h === undefined) return false;
  return h.transcript === true || h.participants.some((p) => p.captions === true);
}

/** How long a caption line stays up after it arrived, and how many lines show at once. */
export const CAPTION_MS = 7000;
export const MAX_CAPTIONS = 3;
/** Length of one microphone clip sent for transcription ("server" mode). */
export const CAPTION_CLIP_MS = 4000;

/** A caption line on screen. */
export type CaptionLine = { id: string; userId: string; name: string; text: string; shownAt: number };

/**
 * A caption arrived (a signal, or my own POST's answer): added once (by id), oldest dropped beyond MAX_CAPTIONS. A
 * speaker's line that follows their previous one within CAPTION_MS continues it (one growing line, like Slack).
 */
export function addCaption(lines: CaptionLine[], c: HuddleCaption, now: number, members: Record<string, Member> = {}): CaptionLine[] {
  if (lines.some((l) => l.id === c.id) || c.text.trim() === "") return lines;
  const live = liveCaptions(lines, now);
  const last = live[live.length - 1];
  const name = members[c.userId]?.displayName ?? c.name;
  if (last !== undefined && last.userId === c.userId) {
    const text = `${last.text} ${c.text.trim()}`;
    // Keep the tail of a long line readable.
    const clipped = text.length > 220 ? `…${text.slice(text.length - 220).replace(/^\S*\s/, "")}` : text;
    return [...live.slice(0, -1), { ...last, id: c.id, text: clipped, shownAt: now }];
  }
  return [...live, { id: c.id, userId: c.userId, name, text: c.text.trim(), shownAt: now }].slice(-MAX_CAPTIONS);
}

/** The lines still up now. */
export function liveCaptions(lines: CaptionLine[], now: number): CaptionLine[] {
  const kept = lines.filter((l) => now - l.shownAt < CAPTION_MS && now >= l.shownAt);
  return kept.length === lines.length ? lines : kept;
}

/** When the next caption line goes away (undefined = none up). */
export function nextCaptionExpiry(lines: CaptionLine[], now: number): number | undefined {
  let next: number | undefined;
  for (const l of lines) {
    const end = l.shownAt + CAPTION_MS;
    if (end > now && (next === undefined || end < next)) next = end;
  }
  return next;
}

/** A microphone clip is worth transcribing: it was loud enough at some point (Whisper invents words in silence). */
export function clipHasSpeech(peakLevel: number): boolean {
  return peakLevel >= SPEAKING_LEVEL;
}

// ---------- Drawing on a shared screen (Slack's screen-share drawing) ----------

/** The pen colours (Slack's palette) — each person draws in one, picked from their id. */
export const DRAW_COLORS = ["#E01E5A", "#2EB67D", "#36C5F0", "#ECB22E", "#9B5DE5", "#FF7A00"] as const;
/** A finished stroke stays this long, then fades out over STROKE_FADE_MS. */
export const STROKE_HOLD_MS = 3000;
export const STROKE_FADE_MS = 1000;
/** Most points kept per stroke, and strokes kept at once. */
export const MAX_STROKE_POINTS = 2000;
export const MAX_STROKES = 200;
/** Eraser reach, as a fraction of the picture's width. */
export const ERASER_RADIUS = 0.02;

/** A stroke on the shared screen, as this client holds it. */
export type LiveStroke = { id: string; userId: string; color: string; points: [number, number][]; done: boolean; updatedAt: number };

/** My pen colour: stable per person. */
export function drawColor(userId: string): string {
  let h = 0;
  for (let i = 0; i < userId.length; i++) h = (h * 31 + userId.charCodeAt(i)) >>> 0;
  return DRAW_COLORS[h % DRAW_COLORS.length]!;
}

/** Applies a drawing change from `userId` (more of a stroke, or erasing). Only the author may extend a stroke. */
export function applyDraw(strokes: LiveStroke[], userId: string, op: HuddleDrawOp, now: number): LiveStroke[] {
  if (op.kind === "erase") {
    const gone = new Set(op.strokeIds);
    const next = strokes.filter((s) => !gone.has(s.id));
    return next.length === strokes.length ? strokes : next;
  }
  const s = op.stroke;
  const pts = pairs(s.points);
  const i = strokes.findIndex((x) => x.id === s.id);
  if (i >= 0) {
    const prev = strokes[i]!;
    if (prev.userId !== userId) return strokes;
    const points = [...prev.points, ...pts].slice(0, MAX_STROKE_POINTS);
    const next = strokes.slice();
    next[i] = { ...prev, points, done: prev.done || s.done === true, updatedAt: now };
    return next;
  }
  const added: LiveStroke = { id: s.id, userId, color: s.color, points: pts.slice(0, MAX_STROKE_POINTS), done: s.done === true, updatedAt: now };
  return [...pruneStrokes(strokes, now), added].slice(-MAX_STROKES);
}

/** Flattened x, y numbers → points (a dangling x is dropped). */
export function pairs(flat: number[]): [number, number][] {
  const out: [number, number][] = [];
  for (let i = 0; i + 1 < flat.length; i += 2) out.push([flat[i]!, flat[i + 1]!]);
  return out;
}

/** Points → the flattened form sent in a HuddleStroke. */
export function flatten(points: [number, number][]): number[] {
  return points.flatMap(([x, y]) => [Math.round(x * 10000) / 10000, Math.round(y * 10000) / 10000]);
}

/** 1 while drawing and for STROKE_HOLD_MS after the pen lifts, then fading to 0 over STROKE_FADE_MS. */
export function strokeOpacity(s: LiveStroke, now: number): number {
  if (!s.done) return 1;
  const t = now - s.updatedAt - STROKE_HOLD_MS;
  if (t <= 0) return 1;
  return t >= STROKE_FADE_MS ? 0 : Math.round((1 - t / STROKE_FADE_MS) * 100) / 100;
}

/** Drops strokes that have faded out (an unfinished stroke idle for a long time counts as done). */
export function pruneStrokes(strokes: LiveStroke[], now: number): LiveStroke[] {
  const next = strokes.filter((s) => strokeOpacity(s.done ? s : now - s.updatedAt > 30_000 ? { ...s, done: true } : s, now) > 0);
  return next.length === strokes.length ? strokes : next;
}

/** Strokes the eraser at `point` touches (any point within `radius`). */
export function strokesHit(strokes: LiveStroke[], point: [number, number], radius = ERASER_RADIUS): string[] {
  const r2 = radius * radius;
  return strokes
    .filter((s) => s.points.some(([x, y]) => (x - point[0]) ** 2 + (y - point[1]) ** 2 <= r2))
    .map((s) => s.id);
}

/** When the drawing next changes by itself (a fade step), or undefined when nothing is fading. */
export function nextStrokeTick(strokes: LiveStroke[], now: number, stepMs = 100): number | undefined {
  let soonest: number | undefined;
  for (const s of strokes) {
    if (!s.done) continue;
    const fadeStart = s.updatedAt + STROKE_HOLD_MS;
    const end = fadeStart + STROKE_FADE_MS;
    if (end <= now) continue;
    const at = now < fadeStart ? fadeStart : Math.min(end, now + stepMs);
    if (soonest === undefined || at < soonest) soonest = at;
  }
  return soonest;
}

// ---------- Recording ----------

/** The longest recording (then it stops and is posted by itself). */
export const MAX_RECORDING_MS = 60 * 60 * 1000;

/** What the phones' recorders film: one picture at a time (the web draws a camera grid instead). */
export type RecordingStage = { userId: string; screen: boolean };

/**
 * The picture a native recording shows now: the shared screen when someone shares (the one already filmed if it's
 * still shared, else the first by user id), else the camera of whoever speaks loudest (at least SPEAKING_LEVEL),
 * else the camera already filmed while it's still on, else the first camera by user id; undefined = no video (the
 * recorder keeps a plain card). `screens`/`cameras` are the user ids with that video now (mine included), `levels`
 * the audio levels by user id (mine included), `current` the stage filmed so far.
 */
export function recordingStage(screens: string[], cameras: string[], levels: Record<string, number>, current: RecordingStage | undefined): RecordingStage | undefined {
  if (screens.length > 0) {
    if (current?.screen === true && screens.includes(current.userId)) return current;
    return { userId: [...screens].sort()[0]!, screen: true };
  }
  if (cameras.length === 0) return undefined;
  let loudest: string | undefined;
  for (const id of [...cameras].sort()) {
    const level = levels[id] ?? 0;
    if (level >= SPEAKING_LEVEL && (loudest === undefined || level > (levels[loudest] ?? 0))) loudest = id;
  }
  if (loudest !== undefined) return current?.screen === false && current.userId === loudest ? current : { userId: loudest, screen: false };
  if (current?.screen === false && cameras.includes(current.userId)) return current;
  return { userId: [...cameras].sort()[0]!, screen: false };
}

/** "Ana is recording this huddle" / "You're recording", or undefined when nobody is. */
export function recordingNotice(h: Huddle | undefined, meId: string, members: Record<string, Member>): MessageDescriptor | undefined {
  const r = h?.recording;
  if (r === undefined) return undefined;
  if (r.userId === meId) return msg("huddle.recording.you");
  const p = h?.participants.find((x) => x.userId === r.userId);
  const name = members[r.userId]?.displayName ?? p?.name ?? "";
  return msg("huddle.recording.other", { name });
}

// ---------- Transcript and AI notes ----------

/** "m:ss" (or "h:mm:ss") since the huddle started. */
export function transcriptTime(startedAt: number, ts: number): string {
  const total = Math.max(0, Math.floor((ts - startedAt) / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${s}` : `${m}:${s}`;
}

/** One speaker's consecutive lines, merged (what the transcript shows). */
export type TranscriptRow = { userId: string; name: string; time: string; text: string };

/** Consecutive lines by the same person within a minute become one row. */
export function transcriptRows(recap: Pick<HuddleRecap, "startedAt" | "transcript">, members: Record<string, Member> = {}): TranscriptRow[] {
  const rows: TranscriptRow[] = [];
  let lastTs = 0;
  for (const l of recap.transcript) {
    const prev = rows[rows.length - 1];
    if (prev !== undefined && prev.userId === l.userId && l.ts - lastTs < 60_000) {
      prev.text = `${prev.text} ${l.text}`;
    } else {
      rows.push({ userId: l.userId, name: members[l.userId]?.displayName ?? l.name, time: transcriptTime(recap.startedAt, l.ts), text: l.text });
    }
    lastTs = l.ts;
  }
  return rows;
}

/** The transcript as the model reads it: `[m:ss] Name: text`, one row per line. */
export function transcriptText(recap: Pick<HuddleRecap, "startedAt" | "transcript">): string {
  return transcriptRows(recap).map((r) => `[${r.time}] ${r.name}: ${r.text}`).join("\n");
}

export type ParsedNotes = { summary: string[]; actionItems: string[]; decisions: string[] };

const NOTE_SECTIONS: [RegExp, keyof ParsedNotes][] = [
  [/^(summary|resumo)$/i, "summary"],
  [/^(action items?|next steps|itens de ação|próximos passos)$/i, "actionItems"],
  [/^(decisions?|decisões|decisão)$/i, "decisions"],
];
const NONE = /^(none|nenhum|nenhuma|n\/a|-)\.?$/i;

/**
 * The model's answer (SUMMARY / ACTION ITEMS / DECISIONS headings, each followed by "- " bullets) → the notes, or
 * undefined when there is no summary. "None" bullets are dropped; text outside a section is ignored.
 */
export function parseHuddleNotes(answer: string): ParsedNotes | undefined {
  const out: ParsedNotes = { summary: [], actionItems: [], decisions: [] };
  let section: keyof ParsedNotes | undefined;
  for (const raw of answer.split(/\r?\n/)) {
    const line = raw.trim();
    if (line === "") continue;
    const heading = line.replace(/^#+\s*/, "").replace(/^\*\*(.*)\*\*$/, "$1").replace(/:$/, "").trim();
    const hit = NOTE_SECTIONS.find(([re]) => re.test(heading));
    if (hit !== undefined) {
      section = hit[1];
      continue;
    }
    const bullet = /^(?:[-*•]|\d+[.)])\s+(.*)$/.exec(line);
    if (section === undefined || bullet === null) continue;
    const text = bullet[1]!.replace(/\*\*/g, "").trim();
    if (text === "" || NONE.test(text)) continue;
    const list = out[section];
    if (list.length < (section === "summary" ? 8 : 20)) list.push(text.slice(0, 500));
  }
  return out.summary.length === 0 ? undefined : out;
}

/** "12 min", "1 h 5 min", "Under a minute". */
export function recapDuration(startedAt: number, endedAt: number): MessageDescriptor {
  const minutes = Math.round((endedAt - startedAt) / 60_000);
  if (minutes < 1) return msg("huddle.recap.short");
  if (minutes < 60) return msg("huddle.recap.minutes", { minutes });
  return msg("huddle.recap.hours", { hours: Math.floor(minutes / 60), minutes: minutes % 60 });
}

/** The huddle message's card, ready to draw. */
export type RecapView = {
  /** Still going (no end yet). */
  live: boolean;
  duration?: MessageDescriptor;
  /** Attendees' names, in join order. */
  people: string[];
  peopleIds: string[];
  notes?: ParsedNotes;
  notesPending: boolean;
  transcript: TranscriptRow[];
};

export function recapView(recap: HuddleRecap, members: Record<string, Member>): RecapView {
  const view: RecapView = {
    live: recap.endedAt === undefined,
    people: recap.participantIds.map((id) => members[id]?.displayName ?? recap.transcript.find((l) => l.userId === id)?.name ?? "").filter((n) => n !== ""),
    peopleIds: recap.participantIds,
    notesPending: recap.notesPending === true && recap.notes === undefined,
    transcript: transcriptRows(recap, members),
  };
  if (recap.endedAt !== undefined) view.duration = recapDuration(recap.startedAt, recap.endedAt);
  if (recap.notes !== undefined) view.notes = { summary: recap.notes.summary, actionItems: recap.notes.actionItems, decisions: recap.notes.decisions };
  return view;
}
